import { useEffect, useState } from 'react';
import Link, { LinkProps } from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';

interface FetchDownloadLinkProps extends Omit<LinkProps, 'href'> {
  href: string; // fetch URL (from /public or anywhere)
  filename?: string; // optional override for download filename
  children: React.ReactNode; // link text / JSX
}

export default function FetchDownloadLink({
  href,
  filename,
  children,
  ...linkProps
}: FetchDownloadLinkProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;

    const fetchFile = async () => {
      try {
        const res = await fetch(href);
        if (!res.ok) {
          throw new Error('Fetch failed');
        }

        const text = await res.text();

        // OPTIONAL: modify content here
        const finalText = text;

        const blob = new Blob([finalText], {
          type: 'text/plain;charset=utf-8',
        });

        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (e) {
        console.error(e);
        setError(true);
      }
    };

    fetchFile();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [href]);

  if (error) {
    return <span>Download unavailable</span>;
  }

  if (!url) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <CircularProgress size={14} />
        <span>Preparing…</span>
      </Stack>
    );
  }

  return (
    <Link
      color="secondary"
      href={url}
      download={filename ?? href.split('/').pop()}
      underline="hover"
      target="_blank"
      rel="noopener noreferrer"
      {...linkProps}
    >
      {children}
    </Link>
  );
}
